import React, { useState } from 'react'
import { colors } from './fieldTheme'
import { FILTERS, filterEntries, filterCounts, groupByDay, entrySubtitle, people } from './fieldActivity'
import { authorOf } from './fieldSummary'
import { shortName } from './fieldLabels'
import { ChevronRight, CheckIcon, AlertIcon, WrenchIcon, CommentIcon, NoResultIcon } from './FieldIcons'
import { BackButton, FlowIllustration, SoftSearchField, Notice } from './FieldUI'

// Mon activité (canvas A2) et onglet Équipe (A3), réservé à qui peut lire l'activité de toute l'équipe.
// Une ligne par action, pastille de la couleur de l'action, résultat écrit en mots ; filtres et recherche
// par numéro d'emplacement. Chaque inventaire se rouvre tel qu'il a été enregistré (A4).

const TYPE_COLORS = { arrival: colors.arrival, departure: colors.departure, free: colors.free, tour: colors.tour }
const RESULT_STYLES = { ok: { background: '#DCFCE7', color: '#14532D' }, gap: { background: '#FEE2E2', color: '#7F1D1D' }, neutral: { background: '#E8EBF5', color: colors.text } }

function EntryIcon({ entry })
{
	let background = colors.tour
	let icon = <FlowIllustration type="tour" size={30} />
	if (entry.kind === 'inventory')
	{
		background = TYPE_COLORS[entry.type] || colors.free
		icon = <FlowIllustration type={entry.type || 'free'} size={30} />
	}
	else if (entry.kind === 'intervention')
	{
		background = entry.detail.indexOf('Ménage') >= 0 ? '#1CB0C4' : '#8124FF'
		icon = <WrenchIcon size={24} color={entry.detail.indexOf('Ménage') >= 0 ? colors.text : '#FFFFFF'} />
	}
	else if (entry.kind === 'comment')
	{
		background = '#0F766E'
		icon = <CommentIcon size={24} />
	}
	return <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{icon}</span>
}

function ResultPill({ result })
{
	const style = RESULT_STYLES[result.tone] || RESULT_STYLES.neutral
	return (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 28, padding: '0 10px 0 6px', borderRadius: 999, background: style.background, color: style.color, fontWeight: 800 }}>
			{result.tone === 'ok' && <CheckIcon size={16} />}
			{result.tone === 'gap' && <AlertIcon size={16} color={style.color} />}
			{result.label}
		</span>
	)
}

function EntryRow({ entry, team, user, onOpen, onManage })
{
	const author = authorOf(entry.user, user)
	const content = (
		<React.Fragment>
			<EntryIcon entry={entry} />
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
				<span style={{ fontSize: 19, fontWeight: 700, overflowWrap: 'break-word' }}>{entry.title}</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>
					{entry.result && <ResultPill result={entry.result} />}
					{entrySubtitle(entry)}
				</span>
				{team && (
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>
						<span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 999, background: colors.primary, color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700 }}>{(shortName(entry.userName) || author.name).charAt(0).toUpperCase()}</span>
						{shortName(entry.userName) || author.name}
					</span>
				)}
			</span>
		</React.Fragment>
	)
	const style = { backgroundImage: 'none', backgroundColor: colors.surface, minHeight: 88, width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px 12px 20px', border: 0, color: colors.text, textAlign: 'left', fontFamily: 'inherit' }
	// Une ligne qui s'ouvre (inventaire, tour en cours...) ou qui a des options (tour terminé) : même ligne
	// cliquable avec le chevron
	const action = onOpen || onManage
	if (!action)
		return <div style={style}>{content}</div>
	return (
		<button type="button" className="field-row" onClick={() => action(entry)} style={{ ...style, cursor: 'pointer' }}>
			{content}
			<ChevronRight />
		</button>
	)
}

function Chip({ pressed, children, onClick })
{
	return (
		<button
			type="button"
			aria-pressed={pressed}
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: pressed ? colors.primary : colors.surface, minHeight: 48, padding: '0 16px', borderRadius: 999, border: pressed ? 0 : '2px solid ' + colors.border, color: pressed ? '#FFFFFF' : colors.text, fontSize: 16, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
		>
			{children}
		</button>
	)
}

// canOpen(entry) : la ligne se rouvre-t-elle (inventaire, tour en cours, commentaire d'un inventaire) ?
// canManage(entry) : la ligne a-t-elle des options (tour terminé qu'un administrateur peut rouvrir) ?
export default function FieldMyActivity({ user, entries, teamAllowed, now, canOpen, onOpen, canManage, onManage, onBack })
{
	const [tab, setTab] = useState('me')
	const [filter, setFilter] = useState('all')
	const [query, setQuery] = useState('')
	const [person, setPerson] = useState(null)
	const team = teamAllowed && tab === 'team'
	const scope = team ? { query, person } : { query, userId: user ? user._id : null }
	const counts = filterCounts(entries, scope)
	const visible = filterEntries(entries, { ...scope, filter })
	const groups = groupByDay(visible, now)
	const teamPeople = people(entries)
	const nothingYet = filterEntries(entries, team ? {} : { userId: user ? user._id : null }).length === 0

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 16, borderBottom: '1px solid ' + colors.separator }}>
				<BackButton label="Accueil" onClick={onBack} />
				<h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: colors.primary, padding: '8px 20px 0' }}>{team ? 'Activité de l\'équipe' : 'Mon activité'}</h1>
				{teamAllowed && (
					<div role="group" aria-label="Voir l'activité de" style={{ margin: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', padding: 4, borderRadius: 999, background: '#E8EBF5' }}>
						{[{ id: 'me', label: 'Moi' }, { id: 'team', label: 'Équipe' }].map(item => (
							<button
								key={item.id}
								type="button"
								aria-pressed={tab === item.id}
								onClick={() => {
									setTab(item.id)
									setPerson(null)
								}}
								style={{ backgroundImage: 'none', backgroundColor: tab === item.id ? colors.primary : 'transparent', minHeight: 48, borderRadius: 999, border: 0, color: tab === item.id ? '#FFFFFF' : colors.primary, fontSize: 17, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
							>
								{item.label}
							</button>
						))}
					</div>
				)}
				<div style={{ padding: '0 20px' }}>
					<SoftSearchField id="field-activity-search" label="Rechercher un emplacement" placeholder="N° d'emplacement, ex. M066" value={query} onChange={setQuery} onClear={() => setQuery('')} />
				</div>
				<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 20px' }}>
					{FILTERS.map(item => <Chip key={item.id} pressed={filter === item.id} onClick={() => setFilter(item.id)}>{item.label + ' (' + counts[item.id] + ')'}</Chip>)}
				</div>
				{team && teamPeople.length > 1 && (
					<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 20px' }}>
						<Chip pressed={!person} onClick={() => setPerson(null)}>Toute l'équipe</Chip>
						{teamPeople.map(item => <Chip key={item.id} pressed={person === item.id} onClick={() => setPerson(item.id)}>{shortName(item.name) || 'Sans nom'}</Chip>)}
					</div>
				)}
			</div>

			<div style={{ padding: '0 20px 32px' }}>
				{groups.map(group => (
					<React.Fragment key={group.label}>
						<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', padding: '24px 0 12px' }}>{group.label}</h2>
						<div style={{ background: colors.surface, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
							{group.entries.map((entry, index) => (
								<div key={entry.id} style={{ borderTop: index > 0 ? '1px solid ' + colors.separator : 0 }}>
									<EntryRow entry={entry} team={team} user={user} onOpen={canOpen(entry) ? onOpen : null} onManage={canManage && canManage(entry) ? onManage : null} />
								</div>
							))}
						</div>
					</React.Fragment>
				))}
				{nothingYet && (
					<div style={{ margin: '0 -20px' }}>
						<Notice icon={<NoResultIcon />} title="Aucune activité pour l'instant" hint={team ? 'Les inventaires, tours et interventions de l\'équipe s\'afficheront ici.' : 'Vos inventaires, tours et interventions s\'afficheront ici.'} />
					</div>
				)}
				{!nothingYet && groups.length === 0 && (
					<div style={{ margin: '0 -20px' }}>
						<Notice icon={<NoResultIcon />} title={query.trim() ? 'Aucune activité pour « ' + query.trim() + ' »' : 'Aucune activité de ce type'} hint="Vérifiez le numéro d'emplacement ou changez de filtre." />
					</div>
				)}
				{groups.length > 0 && <p style={{ padding: '24px 0 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>Rien de plus ancien.</p>}
				<p style={{ padding: '8px 0 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>Version d'essai : l'activité est gardée sur ce téléphone.</p>
			</div>
		</div>
	)
}
